import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Button, Chip,
    TextField, MenuItem, Tabs, Tab, InputAdornment, Avatar,
    LinearProgress
} from '@mui/material';
import {
    Search as SearchIcon, Download as DownloadIcon,
    CheckCircle as PresentIcon, Cancel as AbsentIcon,
    AccessTime as LateIcon, Schedule as HalfDayIcon,
    CalendarMonth as CalendarIcon, Print as PrintIcon
} from '@mui/icons-material';
import { staffAPI } from '../services/api';

const StaffAttendance = () => {
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [searchTerm, setSearchTerm] = useState('');
    const [departmentFilter, setDepartmentFilter] = useState('');
    const [tab, setTab] = useState(0);

    const staffData = [
        { id: 1, name: 'Mr. Rajesh Patil', role: 'Teacher', department: 'Mathematics', checkIn: '08:15 AM', checkOut: '03:30 PM', status: 'Present', hours: '7h 15m' },
        { id: 2, name: 'Mrs. Sunita Sharma', role: 'Teacher', department: 'English', checkIn: '08:30 AM', checkOut: '03:45 PM', status: 'Present', hours: '7h 15m' },
        { id: 3, name: 'Mr. Amit Joshi', role: 'Clerk', department: 'Admin', checkIn: '-', checkOut: '-', status: 'Absent', hours: '-' },
        { id: 4, name: 'Ms. Priya Desai', role: 'Teacher', department: 'Science', checkIn: '09:00 AM', checkOut: '03:30 PM', status: 'Late', hours: '6h 30m' },
        { id: 5, name: 'Mr. Suresh Kumar', role: 'Teacher', department: 'Hindi', checkIn: '08:00 AM', checkOut: '12:30 PM', status: 'Half Day', hours: '4h 30m' },
        { id: 6, name: 'Mrs. Kavita Wagh', role: 'Teacher', department: 'Science', checkIn: '08:20 AM', checkOut: '03:40 PM', status: 'Present', hours: '7h 20m' },
        { id: 7, name: 'Mr. Deepak Jadhav', role: 'Peon', department: 'Support', checkIn: '07:45 AM', checkOut: '04:00 PM', status: 'Present', hours: '8h 15m' },
        { id: 8, name: 'Mrs. Meena Kulkarni', role: 'Teacher', department: 'Social Studies', checkIn: '-', checkOut: '-', status: 'On Leave', hours: '-' },
    ];

    const filteredData = staffData.filter(s => {
        const matchTab = tab === 0 ? true : tab === 1 ? s.status === 'Present' : tab === 2 ? s.status === 'Absent' || s.status === 'On Leave' : s.status === 'Late' || s.status === 'Half Day';
        const matchSearch = !searchTerm || s.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchDept = !departmentFilter || s.department === departmentFilter;
        return matchTab && matchSearch && matchDept;
    });

    const presentCount = staffData.filter(s => s.status === 'Present').length;
    const absentCount = staffData.filter(s => s.status === 'Absent' || s.status === 'On Leave').length;
    const lateCount = staffData.filter(s => s.status === 'Late' || s.status === 'Half Day').length;
    const attendancePct = Math.round((presentCount / staffData.length) * 100);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Present': return 'success';
            case 'Absent': return 'error';
            case 'On Leave': return 'error';
            case 'Late': return 'warning';
            case 'Half Day': return 'info';
            default: return 'default';
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>Staff Attendance</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField type="date" size="small" value={date} onChange={(e) => setDate(e.target.value)} sx={{ width: 160 }} />
                    <Button variant="outlined" startIcon={<PrintIcon />} size="small">Print</Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} size="small">Export</Button>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Total Staff</Typography>
                        <Typography variant="h4" fontWeight={700}>{staffData.length}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ bgcolor: '#e8f5e9' }}><CardContent sx={{ textAlign: 'center' }}>
                        <PresentIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                        <Typography variant="body2" color="text.secondary">Present</Typography>
                        <Typography variant="h4" fontWeight={700} color="success.main">{presentCount}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ bgcolor: '#ffebee' }}><CardContent sx={{ textAlign: 'center' }}>
                        <AbsentIcon sx={{ color: '#e53935', fontSize: 28 }} />
                        <Typography variant="body2" color="text.secondary">Absent/Leave</Typography>
                        <Typography variant="h4" fontWeight={700} color="error.main">{absentCount}</Typography>
                    </CardContent></Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card><CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Attendance %</Typography>
                        <Typography variant="h4" fontWeight={700}>{attendancePct}%</Typography>
                        <LinearProgress variant="determinate" value={attendancePct}
                            sx={{ mt: 0.5, height: 5, borderRadius: 3, bgcolor: '#e0e0e0', '& .MuiLinearProgress-bar': { bgcolor: attendancePct >= 80 ? '#4caf50' : '#ff9800' } }} />
                    </CardContent></Card>
                </Grid>
            </Grid>

            <Card>
                <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tab label={`All (${staffData.length})`} />
                    <Tab label={`Present (${presentCount})`} />
                    <Tab label={`Absent (${absentCount})`} />
                    <Tab label={`Late/Half Day (${lateCount})`} />
                </Tabs>
                <Box sx={{ p: 2 }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" placeholder="Search by name..." value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField fullWidth select size="small" label="Department" value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
                                <MenuItem value="">All</MenuItem>
                                {[...new Set(staffData.map(s => s.department))].map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                            </TextField>
                        </Grid>
                    </Grid>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Staff Member</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Check In</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Check Out</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Hours</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.map(s => (
                                    <TableRow key={s.id} hover sx={{ bgcolor: s.status === 'Absent' || s.status === 'On Leave' ? '#ffebee' : 'inherit' }}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: '#1a9e8f' }}>{s.name.split(' ').pop()[0]}</Avatar>
                                                <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell><Chip label={s.role} size="small" variant="outlined" /></TableCell>
                                        <TableCell>{s.department}</TableCell>
                                        <TableCell>{s.checkIn}</TableCell>
                                        <TableCell>{s.checkOut}</TableCell>
                                        <TableCell>{s.hours}</TableCell>
                                        <TableCell><Chip size="small" label={s.status} color={getStatusColor(s.status)} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Card>
        </Box>
    );
};

export default StaffAttendance;
